import {
  CheckCircleIcon,
  ChevronLeftIcon,
  XIcon,
} from '@heroicons/react/outline';
import { Modal } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import TickOnboarding from 'assets/Icons/TickOnboarding';
import { usePathname } from 'next/navigation';
import React, { memo, useState } from 'react';
import { useRecoilState } from 'recoil';

import { EventDetails } from '@/hooks/useClickEventTracking';
import { getScreenName } from '@/hooks/usePageTracking';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import {
  subscriptionCheckoutEvent,
  subscriptionRazorpayCloseModalEvent,
  subscriptionSuccessfulEvent,
} from '@/components/shared/Gtags';
import { LoadingV2 } from '@/components/shared/LoadingV2';
import { notify } from '@/components/shared/notify';

import { getSubPlanNameLog } from '@/pages/gems-transactions';
import { getUserProfile } from '@/services/user';
import { globalSubscriptionEvent } from '@/utils/Subscription';

import BestValueBadge from '../../BestValueBadge';
import LimitedTimeBadge from '../../LimitedTimeButton';
import { user } from '../../../../atoms/user';
import {
  startSubscription,
  verifySubscription,
} from '../../../../services/Subscribe';
import { isSubscriptionActive } from '../../../../utils';
import {
  IPlan,
  IStartSubscriptionBody,
} from '../../../../utils/interfaces/Subscription';

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  plan?: IPlan;
  autoTrigger?: boolean;
}

const SubscriptionModal: React.FC<Props> = ({ open, setOpen, plan, autoTrigger = false }) => {
  const pathname = usePathname();
  const [loading, setLoading] = useState<boolean>(false);
  const [polling, setPolling] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);

  const [userData, setUserData] = useRecoilState(user);




  const checkoutEvent = (data: {
    subscription_id: string;
    plan_id: number;
  }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'subscription_checkout',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data?.subscription_id.toString(),
        sub_source: 'plan',
        sub_source_id: data.plan_id.toString(),
        sub_source_name: getSubPlanNameLog(plan?.name ?? ''),
        source_name: 'product_subscription',
        ...globalSubscriptionEvent(plan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plan)?.meta,
        },
      };

      subscriptionCheckoutEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const successEvent = (data: { subscription_id: string }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'subscription_successful',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digital',
        source_id: data.subscription_id,
        sub_source: 'plan',
        sub_source_id: plan?.id.toString(),
        sub_source_name: getSubPlanNameLog(plan?.name ?? ''),
        source_name: 'product_subscription',
        ...globalSubscriptionEvent(plan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plan)?.meta,
        },
        // unit: 
      };

      subscriptionSuccessfulEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (error) {
      console.error(error);
    }
  };
  const razorpayCloseModalEvent = (data: {
    subscription_id: string;
    plan_id: number;
  }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'subscription_failed',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data?.subscription_id.toString(),
        sub_source: 'plan',
        sub_source_id: data.plan_id.toString(),
        sub_source_name: getSubPlanNameLog(plan?.name ?? ''),
        source_name: 'product_subscription',
        ...globalSubscriptionEvent(plan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plan)?.meta,
        },
      };

      subscriptionRazorpayCloseModalEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (typeof document === 'undefined') {
        resolve(false);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async (data: IStartSubscriptionBody) => {
    try {
      setLoading(true);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        notify('Razorpay SDK failed to load. Are you online?', 'error');
        return;
      }
      const result = await startSubscription(data);
      checkoutEvent({
        plan_id: plan?.id as number,
        subscription_id: result?.data?.subscription_id.toString(),
      });
      if (!process.env.NEXT_PUBLIC_RAZORPAY_KEY) {
        throw new Error('Missing RAZORPAY_KEY');
      }
      const options = {
        key: process.env.NEXT_PUBLIC_RAZORPAY_KEY || '',
        ...(result.data.mode === 'auto'
          ? { order_id: result.data.razorpay.subscription_id }
          : { order_id: result.data.razorpay.order_id }),
        name: 'Zebralearn',
        description: 'Subscription',
        handler: async function (response: {
          razorpay_payment_id: string;
          razorpay_subscription_id: string;
          razorpay_signature: string;
        }) {
          successEvent({
            subscription_id: result?.data?.subscription_id.toString()
          });
          setLoading(true);
          try {
            await verifySubscription({
              rp_payment_id: response.razorpay_payment_id,
            });
            await startSubscriptionHandle();
            await successSubscription();
          } catch (error: any) {
            notify(formateErrorObject(error), 'error');
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            razorpayCloseModalEvent({
              plan_id: plan?.id as number,
              subscription_id:
                result.data.mode === 'auto'
                  ? result.data.razorpay.subscription_id
                  : result.data.razorpay.order_id,
            });
            if (autoTrigger) {
              setOpen(false);
            }
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const screens = useBreakpoint();
  const successSubscription = async () => {
    setSubscriptionSuccess(true);
    const data = await getUserProfile();
    setUserData(data?.data);
  };

  const physical = plan?.features[0]?.periodicity && plan.rules[0]?.physical;

  const digital = plan?.features[0]?.periodicity && plan.rules[0]?.digital;

  const bookPerMonth = plan?.features[0]?.periodicity
    ? plan?.features?.[0]?.quota * plan?.periodicity
    : null;

  const startSubscriptionHandle = async () => {
    try {
      setLoading(true);
    } catch (error) {
      console.error(error);
    }
  };

  const subscriptionHandle = () => {
    if (!plan?.id) return;

    const data: IStartSubscriptionBody = {
      plan_id: plan?.id,
      change: false,
      immediate: false,
    };

    if (isSubscriptionActive(userData)) {
      data.change = true;
      data.immediate = true;
    }

    handleSubscribe(data);
  };

  const hasTriggeredRef = React.useRef(false);

  React.useEffect(() => {
    if (open && autoTrigger && plan) {
      if (!hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        subscriptionHandle();
      }
    } else if (!open) {
      hasTriggeredRef.current = false;
    }
  }, [open, autoTrigger, plan]);

  return (
    <Modal
      title={null}
      closable={false}
      open={open}
      onCancel={() => {
        setOpen(false);
        setPolling(false);
        setSubscriptionSuccess(false);
      }}
      centered
      footer={null}
      className='m-0'
      classNames={{
        content: 'p-0 rounded-2xl md:w-[500px] w-full',
        wrapper: 'md:text-center text-start',
        body: 'md:w-auto w-dvw',
      }}
    >
      {subscriptionSuccess ? (
        <div className='relative md:block flex flex-col items-center justify-center  border md:border-white/10 border-black md:rounded-2xl md:px-6 md:py-20 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh'>
          <div className='absolute top-0 right-0 m-5'>
            <ButtonDefault
              onClick={() => {
                setOpen(false);
                setPolling(false);
                setSubscriptionSuccess(false);
              }}
              variant={ButtonVariants.UNSTYLED}
              size={0}
              roundedUnstyled
            >
              <XIcon className='w-6 h-6 text-white' />
            </ButtonDefault>
          </div>
          <div className=' w-full flex-1 flex flex-col justify-center'>
            <div className='flex justify-center md:pb-8'>
              <TickOnboarding width={screens.md ? '100' : '64'} />
            </div>
            <span className='text-xl md:text-2xl xl:text-3xl  text-start flex justify-center md:text-center font-semibold text-white'>
              Congratulations{' '}
              <p className='text-xl md:text-3xl xl:text-4xl inline-flex pl-2 -translate-y-0.5 md:-translate-y-2'>
                🎉
              </p>
            </span>

            <div className='text-white/70 text-xs flex  justify-center md:text-lg  lg:text-center w-full '>
              Subscription completed successfully.
            </div>
          </div>
        </div>
      ) : polling || (autoTrigger && loading) ? (
        <div className='relative md:block flex flex-col items-center justify-center  border md:border-white/10 border-black md:rounded-2xl md:p-6 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh'>
          <LoadingV2
            loaderColor='white'
            messages={
              polling
                ? [
                  'Preparing your subscription benefits...',
                  'Activating your premium access...',
                  'Setting up your book deliveries...',
                  'Finalizing your subscription...',
                  'Unlocking exclusive content for you...',
                  'Almost ready to dive into your books!',
                  'You’re all set—just a moment more!',
                ]
                : [
                  'Redirecting to payment gateway...',
                  'Connecting to Razorpay...',
                  'Please do not close this window...',
                ]
            }
          />
        </div>
      ) : (
        <div className='md:block flex flex-col  border md:border-white/10 border-black md:rounded-2xl md:p-6 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh'>
          <div className='py-[11px] pl-2 pr-3 md:hidden flex items-center gap-3'>
            <ButtonDefault
              onClick={() => setOpen(false)}
              variant={ButtonVariants.UNSTYLED}
              size={0}
              roundedUnstyled
            >
              <ChevronLeftIcon className='w-[22px] h-[22px] text-white' />
            </ButtonDefault>
            <p className='text-sx leading-[22px] font-medium text-white'>
              Subscription
            </p>
          </div>
          <div className='flex-1 overflow-auto'>
            <div className='md:flex hidden items-start justify-between mb-6 '>
              {plan?.name === 'Standard' ? (
                <LimitedTimeBadge />
              ) : plan?.name === 'Premium' ? (
                <BestValueBadge />
              ) : (
                <LimitedTimeBadge classes={{ parantClass: 'invisible' }} />
              )}
              <ButtonDefault
                onClick={() => {
                  setOpen(false);
                  setPolling(false);
                  setSubscriptionSuccess(false);
                }}
                variant={ButtonVariants.UNSTYLED}
                size={0}
                roundedUnstyled
              >
                <XIcon className='w-6 h-6 text-white' />
              </ButtonDefault>
            </div>
            <div className='flex flex-col md:gap-2.5 gap-2 md:items-center items-start md:mx-0 md:mt-0 mx-4 mt-6'>
              <p className='md:text-base/6 text-xs/5 font-normal text-primary '>
                Selected Plan
              </p>
              <div className='flex items-center gap-2'>
                <p className='md:text-[40px] text-2xl font-semibold md:leading-[-1px] leading-8 text-white '>
                  {plan?.name}
                </p>
                <div className='md:hidden block '>
                  {plan?.name === 'Standard' ? (
                    <LimitedTimeBadge
                      classes={{
                        textClass: ' text-xs/5 ',
                        parantClass: 'px-2.5 py-0.5',
                      }}
                    />
                  ) : plan?.name === 'Premium' ? (
                    <BestValueBadge
                      classes={{
                        textClass: ' text-xs/5 ',
                        textParant: 'px-2.5 py-0.5 m-px',
                      }}
                    />
                  ) : (
                    <LimitedTimeBadge classes={{ parantClass: 'invisible' }} />
                  )}
                </div>
              </div>
              {plan?.razorpay_plan.amount ? (
                <div className='flex gap-1 items-end'>
                  <div className='flex items-start gap-1'>
                    <p className='text-base text-white md:mt-1 mt-px'>₹</p>
                    <p className='md:text-[32px] text-xl font-semibold md:leading-10 leading-7 text-white'>
                      {plan?.razorpay_plan.amount / 100}
                    </p>
                  </div>
                  <p className='font-normal md:text-base text-xs md:leading-[31px] leading-6 text-white/70'>
                    / {plan.periodicity}
                    {plan.periodicity_type === 'monthly' ? ' Months' : ''}
                  </p>
                </div>
              ) : null}
              {plan?.razorpay_plan.amount && plan.periodicity ? (
                <p className='text-base font-normal text-white'>
                  [ @
                  {(
                    plan?.razorpay_plan.amount /
                    100 /
                    plan.periodicity
                  ).toFixed(0)}
                  / month ]
                </p>
              ) : (
                <></>
              )}
            </div>
            <div className='border-t border-dashed border-white/20 md:my-12 my-5 md:mx-14 mx-4 flex' />
            <div className='flex md:justify-center justify-start md:mb-12 mb-0 md:mx-0 mx-4'>
              <div className='flex flex-col gap-6'>
                <p className='uppercase text-white/40 md:text-sm text-xs leading-[22px] font-medium'>
                  What’s included
                </p>
                <ul className='flex flex-col gap-3'>
                  <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px]'>
                          {physical ? bookPerMonth + ' Hardcover Books' : null}

                          {physical && digital ? ' + ' : null}
                          {digital
                            ? bookPerMonth +
                            ` ${physical && digital ? 'Free' : ''} Digital Books`
                            : null}

                          { }
                        </p>
                        {/* <p className='text-sm font-normal text-grayZl-700 leading-[22px]'>
                          (Claim any {plan?.features[0]?.quota} Book/month)
                        </p> */}
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px] capitalize'>
                          No auto-renewal without your consent.
                        </p>
                      </div>
                    </div>
                  </li>
                  {/* <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px]'>
                          No Hidden Charges
                        </p>
                      </div>
                    </div>
                  </li> */}
                  <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px] capitalize'>
                          Lifetime access of books
                        </p>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px]'>
                          Access on Any Device
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className='flex items-center gap-3 md:pb-8 md:px-8 px-4 py-5 md:border-none border-t-[0.5px] border-grayZl-850'>
            <ButtonDefault
              onClick={() => setOpen(false)}
              className='border border-grayZl-850 md:flex hidden px-6 py-[9px] rounded-lg'
              variant={ButtonVariants.UNSTYLED}
              size={0}
              roundedUnstyled
            >
              Cancel
            </ButtonDefault>
            <ButtonDefault
              disabled={loading}
              loading={loading}
              variant={ButtonVariants.PRIMARY}
              size={0}
              className='flex flex-1 truncate px-6 py-[9px] rounded-lg'
              onClick={subscriptionHandle}
              roundedUnstyled
            >
              {/* {loading ? (
              <Loading3QuartersOutlined className='animate-spin w-5 h-5' />
            ) : null} */}
              Continue to Payment Gateway
            </ButtonDefault>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default memo(SubscriptionModal);
